import {
  Badge,
  BodyText,
  Button,
  Callout,
  Icons,
  Tooltip,
} from "@/components/ui";
import { useParams } from "@tanstack/react-router";
import { CirclePlus } from "lucide-react";
import { useState } from "react";
import { EditableRecordButton } from "../components/EditableRecordButton";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { AlertsEditor } from "./components/AlertsEditor";
import { AlertsSkeleton } from "./components/AlertsSkeleton";
import { useAlerts } from "./network/useAlerts";

export const Alerts = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { data: flags, isLoading, isError } = useAlerts(patientId);
  const [isEditing, setIsEditing] = useState(false);

  const noAlerts = !isLoading && !isError && !flags?.length;

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Alerts"
        actionButtons={[
          <Tooltip tooltipContentText="Add" side="bottom" key="add">
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => setIsEditing(true)}
              aria-label="Add alerts"
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {isLoading && <AlertsSkeleton />}
        {isError && (
          <div className="flex relative flex-col gap-2 px-3 py-2 w-full">
            <Callout
              className="w-full"
              variant="danger"
              title="Something went wrong!"
              description="Please refresh and try again."
            />
          </div>
        )}
        {noAlerts && !isEditing && (
          <div className="flex py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No alerts documented
            </BodyText>
          </div>
        )}
        {!!flags?.length && !isEditing && (
          <EditableRecordButton onClick={() => setIsEditing(true)}>
            <div className="flex flex-row flex-wrap gap-2">
              {flags?.map((flag) => (
                <Badge variant="warning" key={flag.id}>
                  <Icons.TriangleAlert className="h-2 w-2 stroke-warning" />
                  <BodyText
                    textColor="warning"
                    size="sm"
                    className="flex align-middle items-center"
                  >
                    {flag.flagCodeDisplay}
                  </BodyText>
                </Badge>
              ))}
            </div>
          </EditableRecordButton>
        )}
        {isEditing && (
          <AlertsEditor
            existingFlagCodes={flags?.map((flag) => flag.flagCodeValue)}
            patientId={patientId}
            closeEditor={() => setIsEditing(false)}
          />
        )}
      </SectionContent>
    </div>
  );
};
